/** Zoom change per normalized wheel notch */
export const ZOOM_STEP = 0.1;

/** Minimum zoom level */
export const ZOOM_MIN = 1;

/** Maximum zoom level */
export const ZOOM_MAX = 4;

/** Approximate pixels per line for deltaMode=1 */
export const LINE_HEIGHT = 40;

/** Approximate pixels per page for deltaMode=2 */
export const PAGE_HEIGHT = 800;

/**
 * Normalize a wheel event's deltaY into a zoom increment.
 *
 * Converts deltaY to a pixel-equivalent value based on deltaMode,
 * divides by 100 to get a "notch count", multiplies by ZOOM_STEP,
 * and negates the sign so scroll-up (negative deltaY) = zoom in (positive).
 *
 * @param deltaY - The raw deltaY from the WheelEvent
 * @param deltaMode - The deltaMode from the WheelEvent (0=pixels, 1=lines, 2=pages)
 * @returns The zoom increment to add to the current zoom level
 */
export function normalizeWheelDelta(deltaY: number, deltaMode: number): number {
    let pixelDelta: number;

    switch (deltaMode) {
        case 1:
            pixelDelta = deltaY * LINE_HEIGHT;
            break;
        case 2:
            pixelDelta = deltaY * PAGE_HEIGHT;
            break;
        default:
            pixelDelta = deltaY;
            break;
    }

    return -(pixelDelta / 100) * ZOOM_STEP;
}

/**
 * Clamp pan offsets so the displayed image stays within viewport bounds.
 *
 * This is a standalone pure-function version of App.clampPan that accepts
 * all dimensions as parameters instead of reading from component state.
 *
 * @param pan - Current pan offset { x, y }
 * @param zoom - Current zoom level
 * @param viewportWidth - Viewport element width (clientWidth)
 * @param viewportHeight - Viewport element height (clientHeight)
 * @param naturalWidth - Natural image width
 * @param naturalHeight - Natural image height
 * @returns Clamped pan offset { x, y }
 */
export function clampPan(
    pan: { x: number; y: number },
    zoom: number,
    viewportWidth: number,
    viewportHeight: number,
    naturalWidth: number,
    naturalHeight: number
): { x: number; y: number } {
    const fitScale = Math.min(viewportWidth / naturalWidth, viewportHeight / naturalHeight);
    const displayWidth = naturalWidth * fitScale * zoom;
    const displayHeight = naturalHeight * fitScale * zoom;
    const maxOffsetX = Math.max(0, (displayWidth - viewportWidth) * 0.5);
    const maxOffsetY = Math.max(0, (displayHeight - viewportHeight) * 0.5);

    return {
        x: Math.min(maxOffsetX, Math.max(-maxOffsetX, pan.x)),
        y: Math.min(maxOffsetY, Math.max(-maxOffsetY, pan.y))
    };
}
